import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import { SettingsConfirmModal } from "../../components/modal/SettingsConfirmModal";
import { TrashSvg } from "../../components/svg/TrashSvg";
import {
  SupportPriority,
  SupportView as View,
  useCancelSupportScheduleMutation,
  useCheckSupportInboxMutation,
  useDisconnectSupportMailboxMutation,
  useGetSkipSendersQuery,
  useGetSupportAccessQuery,
  useGetSupportConnectUrlMutation,
  useGetSupportMailboxQuery,
  useGetSupportTicketsQuery,
  useRemoveSkipSenderMutation,
} from "../../slices/SupportSlice";
import { escapeHtml } from "../../utils/escapeHtml";
import { timeAgo } from "../settings/team/invites/teamInvites.utils";
import { AutoEmailEditor, AutoEmailsPage } from "./AutoEmailsPage";
import { DemosPage } from "./DemosPage";
import { EmailsPage } from "./EmailsPage";
import {
  CaseStatusSelect,
  HelpDeskHeader,
  HelpDeskStats,
} from "./HelpDeskParts";
import { HelpDeskSettingsPage } from "./HelpDeskSettingsPage";
import { NewFirmsPage } from "./NewFirmsPage";
import { SavedRepliesPage } from "./SavedRepliesPage";
import { scheduledMarker } from "./schedule.utils";
import { ClockIcon } from "./ScheduleSendButton";
import {
  CATEGORY_LABELS,
  categoryPill,
  customerLine,
  displayName,
  EMPTY_TEXT,
  errorText,
  PRIORITIES,
  PRIORITY_LABELS,
  priorityLabel,
  rowNote,
  sourceLabel,
  statusFilterOptions,
  waitingLabel,
} from "./support.utils";
import { SupportReplyWindow, SupportTicketPage } from "./SupportTicketPage";
import { PAGE, PAGE_INNER, PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

/**
 * ProCharted's Help desk (owner 2026-10-25; v2 the same day): support@ emails, "Talk to a person", problem reports and
 * website leads as cases with statuses, plus the New firms onboarding and the automatic emails. Only for the people
 * the server allows (the owner).
 */
export const SupportView = () => {
  const { data: access, isLoading } = useGetSupportAccessQuery();
  if (isLoading) return <LottieLoading />;
  if (!access?.canUse)
    return (
      <div className={"p-10 text-center text-[15px] text-[#6B7185]"}>
        The Help desk is only for the ProCharted support team.
      </div>
    );
  return (
    <Routes>
      <Route index element={<SupportList />} />
      <Route path={"new-firms"} element={<NewFirmsPage />} />
      <Route path={"demos"} element={<DemosPage />} />
      <Route path={"emails"} element={<EmailsPage />} />
      <Route path={"automatic-emails"} element={<AutoEmailsPage />} />
      <Route path={"automatic-emails/:key"} element={<AutoEmailEditor />} />
      <Route path={"saved-replies"} element={<SavedRepliesPage />} />
      <Route path={"settings"} element={<HelpDeskSettingsPage />} />
      <Route path={":ticketId"} element={<SupportTicketPage />} />
    </Routes>
  );
};

const VIEWS: View[] = [
  "needs_you",
  "waiting",
  "solved",
  "closed",
  "all",
  "other",
  "scheduled",
];

const SupportList = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = (
    VIEWS.includes(params.get("view") as View)
      ? params.get("view")
      : "needs_you"
  ) as View;
  const setView = (v: View) => {
    const next = new URLSearchParams(params);
    if (v === "needs_you") next.delete("view");
    else next.set("view", v);
    setParams(next, { replace: true });
  };
  const [typed, setTyped] = useState("");
  // The "✨ Reply" window (owner 2026-10-30): the case's reply area without leaving the list.
  const [replyTo, setReplyTo] = useState<string | null>(null);
  // Scheduled view: Cancel asks once more on the same button.
  const [confirmCancel, setConfirmCancel] = useState<string | null>(null);
  const [cancelSchedule, { isLoading: isCancelling }] =
    useCancelSupportScheduleMutation();
  const doCancel = async (id: string) => {
    try {
      await cancelSchedule(id).unwrap();
      toast("Cancelled. The email is back in the case's reply box.", {
        type: "info",
      });
    } catch (e) {
      toast(errorText(e, "It wasn't cancelled. Please try again."), {
        type: "error",
      });
    }
    setConfirmCancel(null);
  };
  const [q, setQ] = useState("");
  const [priority, setPriority] = useState<SupportPriority | "">("");
  const [category, setCategory] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(typed), 350);
    return () => clearTimeout(t);
  }, [typed]);
  const { data, isFetching, isError } = useGetSupportTicketsQuery(
    { view, q, priority, category },
    { pollingInterval: 60000 },
  );
  const counts: Partial<Record<View, number>> = {
    needs_you: data?.needsYou ?? 0,
    waiting: data?.waiting ?? 0,
    solved: data?.solved ?? 0,
    closed: data?.closed ?? 0,
    other: data?.other ?? 0,
    // Older servers don't count scheduled emails: then the choice isn't offered.
    ...(data?.scheduled != null ? { scheduled: data.scheduled } : {}),
  };
  const rows = data?.tickets || [];
  const filtered = !!(q.trim() || priority || category);

  return (
    <div className={PAGE}>
      <div className={PAGE_INNER}>
        <HelpDeskHeader
          section={"cases"}
          subtitle={
            "Emails to support@procharted.com, \u201cTalk to a person\u201d requests, problem reports and website leads. Nothing is sent to a customer until you click Send."
          }
        />
        <MailboxBar />
        <HelpDeskStats />

        <div className={"mb-3.5 flex flex-wrap items-center justify-end gap-3"}>
          <div className={"flex flex-wrap gap-2"}>
            <label className={"sr-only"} htmlFor={"helpdesk-search"}>
              Search cases
            </label>
            <input
              id={"helpdesk-search"}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={"Search by name, firm, case # or words…"}
              className={
                "h-9 w-[260px] max-w-full rounded-lg border border-[#DCE2EA] bg-white px-3 text-[13.5px] outline-none focus:border-[#6B4EFF]"
              }
            />
            <label className={"sr-only"} htmlFor={"helpdesk-type"}>
              Type
            </label>
            <select
              id={"helpdesk-type"}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={SMALL_BUTTON}>
              <option value={""}>Type: All</option>
              {Object.entries(CATEGORY_LABELS).map(([key, c]) => (
                <option key={key} value={key}>
                  {c.text}
                </option>
              ))}
            </select>
            <label className={"sr-only"} htmlFor={"helpdesk-priority"}>
              Priority
            </label>
            <select
              id={"helpdesk-priority"}
              value={priority}
              onChange={(e) =>
                setPriority(e.target.value as SupportPriority | "")
              }
              className={SMALL_BUTTON}>
              <option value={""}>Priority: All</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p].text}
                </option>
              ))}
            </select>
            <label className={"sr-only"} htmlFor={"helpdesk-status"}>
              Status
            </label>
            <select
              id={"helpdesk-status"}
              value={view}
              onChange={(e) => setView(e.target.value as View)}
              className={SMALL_BUTTON}>
              {statusFilterOptions(counts).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {view === "other" && <SkipList />}

        <div
          className={
            "overflow-hidden rounded-[14px] border border-[#EDEFF3] bg-white"
          }>
          <div
            className={
              "hidden grid-cols-[92px_1.3fr_2.4fr_150px_80px_130px_80px_92px] gap-3 px-4 py-2.5 text-[12px] font-bold uppercase tracking-wide text-[#5D6475] lg:grid"
            }>
            <span>Case</span>
            <span>From</span>
            <span>Question</span>
            <span>Status</span>
            <span>Priority</span>
            <span>Type</span>
            <span>Waiting</span>
            <span className={"sr-only"}>Reply</span>
          </div>
          {isError ? (
            <div className={"px-4 py-10 text-center text-sm text-[#8A90A2]"}>
              The cases couldn&apos;t be loaded. Please refresh the page.
            </div>
          ) : !data && isFetching ? (
            <div className={"py-8"}>
              <LottieLoading />
            </div>
          ) : rows.length === 0 ? (
            <div className={"px-4 py-12 text-center"}>
              <div className={"text-[15px] font-semibold text-[#1D2C3B]"}>
                {filtered ? "Nothing matches" : EMPTY_TEXT[view].title}
              </div>
              <div className={"mt-1 text-sm text-[#8A90A2]"}>
                {filtered
                  ? "Try other words or clear the filters."
                  : EMPTY_TEXT[view].text}
              </div>
            </div>
          ) : (
            rows.map((row) => {
              const pill = categoryPill(row.category);
              const wait = waitingLabel(row);
              const prio = priorityLabel(row.priority);
              const marker = scheduledMarker(row);
              return (
                // A row, not a <button>: it holds the status dropdown.
                <div
                  key={row.id}
                  role={"link"}
                  tabIndex={0}
                  data-testid={"case-row"}
                  onClick={() => navigate(`/support/${row.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/support/${row.id}`);
                  }}
                  className={classNames(
                    "grid w-full cursor-pointer grid-cols-[92px_1fr] gap-x-3 gap-y-1 border-t border-[#EDEFF3] px-4 py-3 text-left hover:bg-[#FAFAFD] lg:grid-cols-[92px_1.3fr_2.4fr_150px_80px_130px_80px_92px] lg:items-center",
                    wait.late ? "bg-[#FFF9EE]" : row.unread && "bg-[#FBFAFF]",
                  )}>
                  <span
                    className={classNames(
                      "font-mono text-[12.5px]",
                      row.unread
                        ? "font-bold text-[#4B32D6]"
                        : "text-[#5D6475]",
                    )}>
                    #{row.code}
                  </span>
                  <div className={"min-w-0"}>
                    <div
                      className={
                        "truncate text-[14px] font-bold text-[#1F2433]"
                      }>
                      {displayName(row)}
                    </div>
                    <div className={"truncate text-[12.5px] text-[#5D6475]"}>
                      {customerLine(row)}
                      {row.knownBy === "stripe" ? " · in Stripe" : ""}
                    </div>
                  </div>
                  <div className={"col-start-2 min-w-0 lg:col-start-auto"}>
                    <div
                      className={
                        "truncate text-[14px] font-semibold text-[#1F2433]"
                      }>
                      {row.subject || "(no subject)"}
                    </div>
                    <div className={"truncate text-[12.5px] text-[#5D6475]"}>
                      {row.notSure && (
                        <span
                          className={
                            "mr-1.5 rounded bg-[#FFF4E0] px-1.5 py-px text-[11.5px] font-bold text-[#8A5300]"
                          }>
                          Not sure this is a customer
                        </span>
                      )}
                      {marker && (
                        <span
                          data-testid={"scheduled-marker"}
                          className={classNames(
                            "mr-1.5 inline-flex items-center gap-1 rounded px-1.5 py-px text-[11.5px] font-bold",
                            marker.failed
                              ? "bg-[#FDECEA] text-[#B42318]"
                              : "bg-[#EFEBFF] text-[#4B32D6]",
                          )}>
                          <ClockIcon />
                          {marker.text}
                        </span>
                      )}
                      {rowNote(row)}
                    </div>
                  </div>
                  <span className={"col-start-2 min-w-0 lg:col-start-auto"}>
                    <CaseStatusSelect
                      ticket={row}
                      label={`Status of case #${row.code}`}
                    />
                    {row.rating && (
                      <span className={"ml-1.5 text-[11.5px] text-[#5D6475]"}>
                        {row.rating === "good" ? "rated good" : "rated bad"}
                      </span>
                    )}
                  </span>
                  <span
                    className={classNames(
                      "col-start-2 text-[13px] font-semibold lg:col-start-auto",
                      prio.className,
                    )}>
                    {prio.text}
                  </span>
                  <span
                    className={
                      "col-start-2 flex flex-wrap items-center gap-1 text-[12.5px] text-[#3A4256] lg:col-start-auto"
                    }>
                    {pill.text}
                    {row.source !== "email" && (
                      <span className={"text-[11.5px] text-[#8A90A2]"}>
                        · {sourceLabel(row.source)}
                      </span>
                    )}
                  </span>
                  <span
                    title={timeAgo(row.lastMessageAt)}
                    className={classNames(
                      "col-start-2 text-[13px] lg:col-start-auto",
                      wait.late ? "font-bold text-[#B45309]" : "text-[#3A4256]",
                    )}>
                    {wait.text}
                  </span>
                  <span className={"col-start-2 lg:col-start-auto"}>
                    {view === "scheduled" && row.scheduledStatus ? (
                      <button
                        type={"button"}
                        data-testid={"case-cancel-schedule"}
                        disabled={
                          isCancelling || row.scheduledStatus === "sending"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirmCancel === row.id) void doCancel(row.id);
                          else setConfirmCancel(row.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            e.stopPropagation();
                        }}
                        title={
                          "Nothing is sent; the text goes back into the case's reply box"
                        }
                        className={
                          "inline-flex h-8 items-center rounded-lg border border-[#F3C2BC] bg-white px-2.5 text-[12.5px] font-bold text-[#C0392B] hover:bg-[#FFF6F5] disabled:opacity-60"
                        }>
                        {confirmCancel === row.id ? "Yes, cancel" : "Cancel"}
                      </button>
                    ) : (
                      <button
                        type={"button"}
                        data-testid={"case-reply"}
                        onClick={(e) => {
                          e.stopPropagation();
                          setReplyTo(row.id);
                        }}
                        onKeyDown={(e) => {
                          // Enter/Space press this button, not the row (which opens the case); Esc still closes the window.
                          if (e.key === "Enter" || e.key === " ")
                            e.stopPropagation();
                        }}
                        title={"Reply here, with an answer already written"}
                        className={
                          "inline-flex h-8 items-center gap-1 rounded-lg border border-[#DCD3FF] bg-[#F4F1FF] px-2.5 text-[12.5px] font-bold text-[#4B32D6] hover:bg-[#EBE5FF]"
                        }>
                        ✨ Reply
                      </button>
                    )}
                  </span>
                </div>
              );
            })
          )}
          {data?.more && (
            <div
              className={
                "border-t border-[#EDEFF3] px-4 py-3 text-center text-[12.5px] text-[#5D6475]"
              }>
              Showing the newest 200. Search to find older cases.
            </div>
          )}
        </div>
      </div>
      {replyTo && (
        <SupportReplyWindow
          ticketId={replyTo}
          onClose={() => setReplyTo(null)}
        />
      )}
    </div>
  );
};

/** "Always skip" senders (Other mail tab): remove one to let its emails in again. */
const SkipList = () => {
  const { data } = useGetSkipSendersQuery();
  const [remove] = useRemoveSkipSenderMutation();
  if (!data || data.length === 0)
    return (
      <div
        className={
          "mb-3 rounded-[14px] border border-[#EDEFF3] bg-white px-4 py-3 text-[13px] text-[#5D6475]"
        }>
        <b className={"text-[#1F2433]"}>Always-skip list:</b> empty. Open an
        email here and click <b>Not a customer</b> to skip its sender or domain
        from now on.
      </div>
    );
  return (
    <div
      className={
        "mb-3 rounded-[14px] border border-[#EDEFF3] bg-white px-4 py-3 text-[13px] text-[#3A4256]"
      }>
      <b className={"text-[#1F2433]"}>Always skipped ({data.length}):</b>{" "}
      <span className={"inline-flex flex-wrap gap-1.5 align-middle"}>
        {data.map((s) => (
          <span
            key={s.id}
            className={
              "inline-flex items-center gap-1 rounded-full bg-[#F1F2F5] py-0.5 pl-2.5 pr-1"
            }>
            {s.pattern.startsWith("@") ? `anyone ${s.pattern}` : s.pattern}
            <button
              type={"button"}
              aria-label={`Stop skipping ${s.pattern}`}
              onClick={() =>
                remove(s.id)
                  .unwrap()
                  .then(() =>
                    toast(`${s.pattern} is no longer skipped.`, {
                      type: "success",
                    }),
                  )
                  .catch((e) =>
                    toast(errorText(e, "Couldn't remove it."), {
                      type: "error",
                    }),
                  )
              }
              className={
                "rounded-full px-1.5 text-[#5D6475] hover:bg-white hover:text-[#B42318]"
              }>
              ×
            </button>
          </span>
        ))}
      </span>
    </div>
  );
};

/** support@ connection: Connect (Microsoft 365 sign-in through Nylas), Check now, Disconnect, last error. */
const MailboxBar = () => {
  const { data: box, isLoading } = useGetSupportMailboxQuery(undefined, {
    pollingInterval: 120000,
  });
  const [getUrl, { isLoading: isConnecting }] =
    useGetSupportConnectUrlMutation();
  const [checkNow, { isLoading: isChecking }] = useCheckSupportInboxMutation();
  const [disconnect, { isLoading: isDisconnecting }] =
    useDisconnectSupportMailboxMutation();
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const connect = async () => {
    try {
      const { url } = await getUrl({
        provider: "microsoft",
        returnUrl: `${window.location.origin}/support`,
      }).unwrap();
      window.location.assign(url);
    } catch (error) {
      toast(errorText(error, "Couldn't start connecting. Please try again."), {
        type: "error",
      });
    }
  };

  if (isLoading || !box) return null;
  return (
    <>
      <div
        className={classNames(
          "mb-4 flex flex-wrap items-center gap-3 rounded-[14px] border px-4 py-3 text-[13.5px]",
          box.connected && !box.lastError
            ? "border-[#D6EFE0] bg-[#F6FBF8]"
            : "border-[#F5DEB0] bg-[#FFF9EE]",
        )}>
        <span
          className={classNames(
            "h-2.5 w-2.5 shrink-0 rounded-full",
            box.connected && !box.lastError ? "bg-[#22A65A]" : "bg-[#F0A23B]",
          )}
        />
        <div className={"min-w-[220px] flex-1 text-[#3A4256]"}>
          {box.connected ? (
            <>
              <b className={"text-[#1F2433]"}>
                {box.email || "Support mailbox"}
              </b>{" "}
              is connected
              {box.lastCheckedAt
                ? ` · checked ${timeAgo(box.lastCheckedAt)}`
                : " · first check within 2 minutes"}
              {box.lastError && (
                <div className={"mt-0.5 text-[#9A5B00]"}>{box.lastError}</div>
              )}
            </>
          ) : (
            <>
              <b className={"text-[#1F2433]"}>
                The support mailbox isn&apos;t connected.
              </b>{" "}
              Connect support@procharted.com to bring its emails here. Sign in
              with that mailbox&apos;s Microsoft 365 account. (Onboarding emails
              still go out from support@ without it.)
            </>
          )}
          {!box.aiConfigured && (
            <div className={"mt-0.5 text-[#9A5B00]"}>
              AI isn&apos;t set up on this server, so emails arrive without
              drafts.
            </div>
          )}
        </div>
        {box.connected ? (
          <div className={"flex gap-2"}>
            <button
              type={"button"}
              disabled={isChecking}
              onClick={async () => {
                try {
                  const { added } = await checkNow().unwrap();
                  toast(
                    added
                      ? `${added} new email${added === 1 ? "" : "s"}.`
                      : "No new emails.",
                    { type: "success" },
                  );
                } catch (error) {
                  toast(errorText(error, "Couldn't check the inbox."), {
                    type: "error",
                  });
                }
              }}
              className={SMALL_BUTTON}>
              {isChecking ? "Checking…" : "Check now"}
            </button>
            {box.lastError && (
              <button
                type={"button"}
                onClick={() => void connect()}
                className={SMALL_BUTTON}>
                Connect again
              </button>
            )}
            <button
              type={"button"}
              onClick={() => setConfirmDisconnect(true)}
              className={classNames(SMALL_BUTTON, "text-[#C0392B]")}>
              Disconnect
            </button>
          </div>
        ) : (
          <button
            type={"button"}
            disabled={isConnecting || !box.emailConfigured}
            title={
              box.emailConfigured
                ? undefined
                : "Email (Nylas) isn't set up on this server."
            }
            onClick={() => void connect()}
            className={PRIMARY_BUTTON}>
            {isConnecting ? "Opening…" : "Connect support@procharted.com"}
          </button>
        )}
      </div>
      {confirmDisconnect && (
        <SettingsConfirmModal
          onClose={() => setConfirmDisconnect(false)}
          onConfirm={async () => {
            await disconnect()
              .unwrap()
              .catch(() => undefined);
            setConfirmDisconnect(false);
          }}
          isLoading={isDisconnecting}
          icon={<TrashSvg />}
          title={"Disconnect the support mailbox?"}
          description={escapeHtml(
            "New emails stop arriving here and you can't send replies until you connect it again. Conversations already here stay.",
          )}
          confirmText={"Disconnect"}
          variant={"danger"}
        />
      )}
    </>
  );
};
